import { useEffect, useState, useRef } from 'react';
import { getAlarmDashboard, getAlarm } from '../services/alarms/alarm.service';
import type { Alarm } from '../models';
import type { AlarmDashboardResponse, AlarmItem } from '../services/alarms/alarm.types';

// Internal cache to avoid duplicate dashboard fetches across components
let cachedAlarms: Alarm[] | null = null;
let inFlight: Promise<Alarm[]> | null = null;
// Cache detail lookups to avoid duplicate network requests per alarm id
const detailCache: Record<string, Promise<void>> = {};

function deriveId(a: AlarmItem, index: number): string {
  if (a.id) return String(a.id);
  const base = `${a.pathToAlarm || a.alarmMessage || 'alarm'}|${a.alarmDateTime || ''}|${index}`;
  let hash = 0;
  for (let i = 0; i < base.length; i++) {
    hash = (hash * 31 + (base.codePointAt(i) ?? 0)) >>> 0;
  }
  return `gen_${hash.toString(16)}`;
}

export interface UseAlarmsResult {
  alarms: Alarm[] | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// Map dashboard response into Alarm[]
function mapDashboardData(data: AlarmDashboardResponse): Alarm[] {
  const ack = Array.isArray(data.acknowledgedSummary?.alarms) ? data.acknowledgedSummary.alarms : [];
  const unack = Array.isArray(data.unacknowledgedSummary?.alarms) ? data.unacknowledgedSummary.alarms : [];
  const all = [...ack, ...unack];
  return all.map((a: AlarmItem, idx: number) => {
    let rawStateName: string | undefined = a.alarmStateName ?? undefined;
    if (!rawStateName && typeof a.processingStatus === 'string' && /active|inactive/i.test(a.processingStatus)) {
      rawStateName = a.processingStatus;
    }
    let normalizedStateName: string;
    if (rawStateName) {
      normalizedStateName = rawStateName.charAt(0).toUpperCase() + rawStateName.slice(1).toLowerCase();
    } else {
      normalizedStateName = a.acknowledged ? 'Inactive' : 'Active';
    }

    const apiId = a.alarmId ?? a.id;
    const assignedId = (apiId === null || apiId === undefined) ? deriveId(a, idx) : String(apiId);

    return ({
      properties: {
        id: assignedId,
        siteName: a.siteName || undefined,
        siteGuid: a.siteGuid ?? null,
        name: a.pathToAlarm || a.alarmMessage || `Alarm ${a.id ?? idx}`,
        status: a.acknowledged ? 'Acknowledged' : 'Unacknowledged',
        priority: a.priority ?? 0,
        path: a.pathToAlarm ?? '',
        message: a.alarmMessage ?? '',
        date: a.alarmDateTime ?? new Date().toISOString(),
        acknowledged: Boolean(a.acknowledged),
        processingStatus: a.processingStatus || 'Unknown',
        alarmStateId: a.alarmStateId,
        alarmStateName: normalizedStateName
      }
    });
  });
}

// Enrich site names with limited concurrency
async function enrichSiteNames(mapped: Alarm[]): Promise<void> {
  const toEnrich = mapped.filter(m =>
    !m.properties.siteName &&
    m.properties.id &&
    !String(m.properties.id).startsWith('gen_')
  );
  if (toEnrich.length === 0) return;

  const limit = 4;
  for (let i = 0; i < toEnrich.length; i += limit) {
    const batch = toEnrich.slice(i, i + limit);
    await Promise.all(batch.map(async alarmRef => {
      const alarmId = String(alarmRef.properties.id);
      if (alarmId.startsWith('gen_')) return;
      if (Object.hasOwn(detailCache, alarmId)) {
        await detailCache[alarmId];
        return;
      }
      detailCache[alarmId] = (async () => {
        try {
          const detail = await getAlarm(alarmId);
            if (detail.success) {
              const sn = detail.data?.siteName || detail.data?.siteIdentifier;
              if (sn) alarmRef.properties.siteName = sn;
            }
        } catch {
          // ignore
        }
      })();
      await detailCache[alarmId];
    }));
  }
}

export function useAlarms(): UseAlarmsResult {
  const [alarms, setAlarms] = useState<Alarm[] | null>(cachedAlarms);
  const [loading, setLoading] = useState<boolean>(!cachedAlarms);
  const [error, setError] = useState<string | null>(null);
  const refreshRequested = useRef(false);

  const load = () => {
    inFlight ??= (async () => {
      const res = await getAlarmDashboard({ page: 1, pageSize: 1000 });
      if (!res.success || !res.data) throw new Error(res.error?.message || 'Failed to load alarm dashboard');
      const mapped = mapDashboardData(res.data);
      await enrichSiteNames(mapped);
      cachedAlarms = mapped;
      return mapped;
    })().finally(() => { inFlight = null; });
    return inFlight;
  };

  useEffect(() => {
    let cancelled = false;
    if (!cachedAlarms || refreshRequested.current) {
      setLoading(true);
      setError(null);
      load()
        .then(list => { if (!cancelled) setAlarms(list); })
        .catch(e => { if (!cancelled) setError(e.message || 'Error loading alarms'); })
        .finally(() => { if (!cancelled) { setLoading(false); refreshRequested.current = false; } });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => {
    refreshRequested.current = true;
    cachedAlarms = null;
    setLoading(true);
    setError(null);
    load()
      .then(list => setAlarms(list))
      .catch(e => setError(e.message || 'Error loading alarms'))
      .finally(() => { setLoading(false); refreshRequested.current = false; });
  };

  return { alarms, loading, error, refresh };
}
